<template>
  <div v-if="files && files.length" class="ta-uploaded-files">
    <div 
      v-for="(file, index) in files" 
      :key="file.uuid || file.attachment_uuid || index" 
      class="ta-uploaded-file-row"
      :class="{ 'ta-file-marked-for-deletion': isFileMarkedForDeletion(file) }"
    >
      <div class="ta-file-main">
        <a 
          :href="file.url || file.path || '#'" 
          @click.prevent="openAttachment(file)" 
          target="_blank" 
          rel="noopener noreferrer"
          :class="{ 'ta-file-link-disabled': isFileMarkedForDeletion(file) }"
        >
          {{ file.name }}
        </a>
        <small class="text-muted ml-2">({{ formatFileSize(file.size) }})</small>
        <span v-if="isFileMarkedForDeletion(file)" class="ta-deletion-indicator ml-2">
          <i class="fas fa-trash-alt"></i>
          <span class="ml-1">{{ $t('Will be deleted') }}</span>
        </span>
      </div>
      <div class="ta-file-actions">
        <button 
          type="button" 
          class="btn btn-link p-0 ta-remove-link" 
          @click.prevent.stop="onToggleDeletion(file)"
          :title="isFileMarkedForDeletion(file) ? $t('Restore file') : $t('Mark for deletion')"
          :disabled="disabled"
        >
          {{ isFileMarkedForDeletion(file) ? $t('Restore') : $t('Remove') }}
        </button>
      </div>
    </div>
  </div>
</template>

<script>
import { mapActions, mapMutations } from 'vuex'

export default {
  name: 'UploadedFilesList',
  inject: {
    disabled: {
      default: false
    }
  },
  props: {
    files: {
      type: Array,
      required: true
    },
    deletedFiles: {
      type: Array,
      default: () => []
    }
  },
  methods: {
    ...mapActions(['getPresignedUrlForAttachment']),
    ...mapMutations(['showNotification']),
    isFileMarkedForDeletion(file) {
      const fileId = file.attachment_uuid || file.uuid
      return this.deletedFiles.includes(fileId)
    },
    onToggleDeletion(file) {
      if (this.disabled) {
        return
      }
      if (this.isFileMarkedForDeletion(file)) {
        this.$emit('restore', file)
      } else {
        this.$emit('mark-for-deletion', file)
      }
    },
    onRemove(file) {
      this.$emit('remove', file)
    },
    formatFileSize(bytes) {
      if (!bytes && bytes !== 0) return ''
      if (bytes === 0) return '0 Bytes'
      const k = 1024
      const sizes = ['Bytes', 'KB', 'MB', 'GB']
      const i = Math.floor(Math.log(bytes) / Math.log(k))
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
    },
    async openAttachment(file) {
      // Don't allow opening files marked for deletion
      if (this.isFileMarkedForDeletion(file)) {
        this.showNotification({
          title: this.$t('File marked for deletion'),
          text: this.$t('This file is marked for deletion and cannot be opened. Restore it first if you need to view it.'),
          variant: 'warning'
        })
        return
      }

      try {
        // Get the attachment UUID from the file object
        const attachmentUuid = file.attachment_uuid
        
        if (!attachmentUuid) {
          console.error('No attachment UUID found for file:', file)
          this.showNotification({
            title: this.$t('Error'),
            text: this.$t('Unable to open attachment: missing file identifier'),
            variant: 'danger'
          })
          return
        }

        // Show loading state
        this.showNotification({
          title: this.$t('Loading'),
          text: this.$t('Loading attachment...'),
          variant: 'info'
        })

        // Get presigned URL from backend
        const response = await this.getPresignedUrlForAttachment({
          attachment_uuid: attachmentUuid
        })

        if (response && response.presigned_url) {
          // Open the presigned URL in a new tab
          window.open(response.presigned_url, '_blank', 'noopener,noreferrer')
        } else {
          throw new Error('No presigned URL received from server')
        }
      } catch (error) {
        console.error('Error opening attachment:', error)
        this.showNotification({
          title: this.$t('Error'),
          text: this.$t('Unable to open attachment. Please try again.'),
          variant: 'danger'
        })
      }
    }
  }
}
</script>

<style scoped>
.ta-uploaded-file-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-top: 0.25rem;
  transition: all 0.3s ease;
}

.ta-file-marked-for-deletion {
  opacity: 0.6;
  background-color: #f8f9fa;
  border-left: 3px solid #dc3545;
  padding-left: 8px;
  border-radius: 4px;
}

.ta-file-link-disabled {
  color: #6c757d !important;
  text-decoration: line-through;
  pointer-events: none;
  cursor: not-allowed;
}

.ta-deletion-indicator {
  color: #dc3545;
  font-size: 0.875rem;
  font-weight: 500;
}

.ta-remove-link {
  color: #d9534f;
}
.ta-remove-link:hover {
  text-decoration: underline;
}

.ta-file-marked-for-deletion .ta-remove-link {
  color: #28a745;
}
.ta-file-marked-for-deletion .ta-remove-link:hover {
  color: #1e7e34;
}

.ta-remove-link:disabled {
  color: #6c757d !important;
  cursor: not-allowed;
  opacity: 0.6;
}

.ta-remove-link:disabled:hover {
  text-decoration: none;
  color: #6c757d !important;
}
</style>


